import React from 'react';
import { Card, Button, Typography, Row, Col, Tag, Space } from 'antd';
import {
  PhoneOutlined,
  UsergroupAddOutlined,
  MessageOutlined,
  GiftOutlined,
  CalendarOutlined,
  ShareAltOutlined,
  EnvironmentOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import 'moment-timezone';
import { atcb_action } from 'add-to-calendar-button';

const { Title, Paragraph, Text } = Typography;

const ConfirmationScreen = ({ config, reservation, onReset, onAddAnother }) => {
  const { company_name, company_phone, company_address } = config;

  const mapEmbedUrl = `https://www.google.com/maps/embed/v1/place?q=${encodeURIComponent(
    company_address
  )}&key=YOUR_API_KEY`; // Replace with real key

  const shareData = {
    title: `Reservation at ${company_name}`,
    text: `I have a reservation at ${company_name}. Address: ${company_address}. Call: ${company_phone}`,
    url: `https://maps.google.com/?q=${encodeURIComponent(company_address)}`,
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Share cancelled or failed', err);
      }
    } else {
      alert('Sharing not supported in this browser.');
    }
  };

  const downloadICS = () => {
    const start = moment(reservation.start).utc().format('YYYYMMDDTHHmmss') + 'Z';
    const end = moment(reservation.end).utc().format('YYYYMMDDTHHmmss') + 'Z';

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
SUMMARY:Reservation at ${company_name}
DTSTART:${start}
DTEND:${end}
LOCATION:${company_address}
DESCRIPTION:Party of ${reservation.partySize}. ${reservation.notes ? `Notes: ${reservation.notes}` : ''} ${reservation.occasion ? `Occasion: ${reservation.occasion}` : ''}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'reservation.ics';
    link.click();
  };

  return (
    <div className="confirmation-screen">
      {/* ✅ Different message for reschedule vs booking */}
      <Title level={3} className="confirm-title">
        {reservation.isEdit ? "Reservation Rescheduled ✅" : "Booking Confirmed ✅"}
      </Title>
      <Paragraph className="confirm-subtitle">
        {reservation.isEdit
          ? `Your reservation at ${company_name} has been successfully rescheduled.`
          : `Your reservation at ${company_name} is confirmed! We're excited to see you.`}
      </Paragraph>

      {/* Reservation Details */}
      <Card bordered={false} className="confirm-card">
        <Title level={4} className="section-title">Reservation Details</Title>
        <Row gutter={[0, 12]}>
          <Col span={24}>
            <CalendarOutlined className="icon" />
            <Text strong>Date & Time:</Text>{' '}
            {moment(reservation.start).format('MMM D, YYYY h:mm A')} –{' '}
            {moment(reservation.end).format('h:mm A')}
          </Col>
          <Col span={24}>
            <UsergroupAddOutlined className="icon" />
            <Text strong>Party Size:</Text> {reservation.partySize}
          </Col>
          <Col span={24}>
            <Text strong>Status:</Text>{' '}
            <Tag
              color={
                reservation.status === 'Confirmed'
                  ? config?.colors?.primary || 'green'
                  : reservation.status === 'Cancelled'
                  ? 'red'
                  : reservation.status === 'Pending'
                  ? 'orange'
                  : 'default'
              }
            >
              {reservation.status || 'Awaited'}
            </Tag>
          </Col>
          {reservation.occasion && (
            <Col span={24}>
              <GiftOutlined className="icon" />
              <Text strong>Occasion:</Text> {reservation.occasion}
            </Col>
          )}
          {reservation.notes && (
            <Col span={24}>
              <MessageOutlined className="icon" />
              <Text strong>Special Requests:</Text> {reservation.notes}
            </Col>
          )}
        </Row>
      </Card>

      {/* ✅ Actions placed here */}
      <Row gutter={16} className="action-buttons">
        <Col span={12}>
          <Button type="primary" block size="large" onClick={onReset} className="primary-btn">
            Go to My Dashboard
          </Button>
        </Col>
        <Col span={12}>
          <Button type="default" block size="large" onClick={onAddAnother} className="secondary-btn">
            Make Another Reservation
          </Button>
        </Col>
      </Row>

      {/* Company Info + Map */}
      <Card bordered={false} className="confirm-card">
        <Title level={4} className="section-title">{company_name}</Title>
        <Paragraph>{company_address}</Paragraph>
        <Button
          icon={<PhoneOutlined />}
          type="link"
          href={`tel:${company_phone}`}
          className="link-btn"
        >
          Call Us: {company_phone}
        </Button>
        <iframe
          src={mapEmbedUrl}
          width="100%"
          height="220"
          className="map-frame"
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </Card>

      {/* 🎉 Prominent Share Section */}
      <Card bordered={false} className="confirm-card share-card">
        <h3 className="share-title">📢 Share & Save</h3>
        <Paragraph className="share-subtitle">
          Add your reservation to your calendar, share with friends, or get directions.
        </Paragraph>
        <Space wrap size="middle" className="share-buttons">
          <Button icon={<EnvironmentOutlined />} className="btn-directions">
            Directions
          </Button>
          <Button icon={<ShareAltOutlined />} className="btn-share" onClick={handleShare}>
            Share Info
          </Button>
          <Button className="btn-calendar" onClick={() =>
            atcb_action({
              name: `Reservation at ${company_name}`,
              startDate: moment(reservation.start).format('YYYY-MM-DD'),
              startTime: moment(reservation.start).format('HH:mm'),
              endTime: moment(reservation.end).format('HH:mm'),
              location: company_address,
              description: `Party of ${reservation.partySize}. ${reservation.notes ? `Notes: ${reservation.notes}` : ''} ${reservation.occasion ? `Occasion: ${reservation.occasion}` : ''}`,
              options: ['Google', 'Apple', 'Outlook.com', 'Yahoo'],
              timeZone: 'current',
            })
          }>
            Add to Calendar
          </Button>
          <Button icon={<DownloadOutlined />} className="btn-download" onClick={downloadICS}>
            Download ICS
          </Button>
        </Space>
      </Card>

      {/* Scoped CSS */}
      <style>{`
        .confirmation-screen {
          max-width: 650px;
          margin: 40px auto;
          padding: 20px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 6px 18px rgba(0,0,0,0.08);
          font-family: ${config?.font || 'Arial, sans-serif'};
        }
        .confirm-title {
          text-align: center;
          color: ${config?.colors?.primary || '#2563eb'};
          margin-bottom: 8px;
        }
        .confirm-subtitle {
          text-align: center;
          color: #555;
          margin-bottom: 24px;
        }
        .confirm-card {
          border-radius: 10px;
          margin-bottom: 20px;
          padding: 16px;
          background: #fafafa;
        }
        .section-title {
          color: #222;
          margin-bottom: 12px;
        }
        .icon {
          color: ${config?.colors?.primary || '#2563eb'};
          margin-right: 6px;
        }
        .link-btn {
          padding-left: 0;
        }
        .map-frame {
          margin-top: 12px;
          border-radius: 8px;
          border: none;
        }
        /* ✅ Action Buttons */
        .action-buttons {
          margin: 24px 0;
        }
        .primary-btn {
          background: linear-gradient(135deg, ${config?.colors?.primary || "#2563eb"} 0%, ${config?.colors?.buttonHover || "#1d4ed8"} 100%);
          border: none;
          color: #fff;
          font-weight: 600;
        }
        .secondary-btn {
          border: 2px solid ${config?.colors?.primary || "#2563eb"};
          color: ${config?.colors?.primary || "#2563eb"};
          font-weight: 600;
        }
        .secondary-btn:hover {
          background: rgba(37, 99, 235, 0.05);
        }
        /* 🎉 Share Section */
        .share-card {
          text-align: center;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
          padding: 24px;
        }
        .share-title {
          font-size: 1.4rem;
          font-weight: 800;
          margin-bottom: 12px;
          background: linear-gradient(90deg, #6366f1, #ec4899);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .share-subtitle {
          color: #6b7280;
          margin-bottom: 20px;
        }
        .share-buttons .ant-btn {
          border-radius: 8px;
          font-weight: 600;
          min-width: 160px;
          padding: 12px 18px;
          color: #fff;
          border: none;
        }
        /* Color-coded share buttons */
        .btn-directions { background: #10b981; } /* emerald */
        .btn-share { background: #6366f1; }      /* indigo */
        .btn-calendar { background: #f59e0b; }   /* amber */
        .btn-download { background: #6b7280; }   /* gray */
        .btn-directions:hover { background: #059669; }
        .btn-share:hover { background: #4f46e5; }
        .btn-calendar:hover { background: #d97706; }
        .btn-download:hover { background: #4b5563; }
      `}</style>
    </div>
  );
};

export default ConfirmationScreen;
